import { useState, type ReactNode } from 'react';
import { passbookLogoUrl } from '@/shared/lib/passbook-utils';
import { ThemeToggle } from '@/shared/components/theme-toggle';
import { cn } from '@/shared/lib/cn';
import type { PublicInvitePassbook } from '../api/use-public-invite';
import { PublicInviteFooter } from './public-invite-footer';

interface PublicInviteLayoutProps {
  passbook: PublicInvitePassbook;
  children: ReactNode;
  /** Área do participante: os logos (loja no topo, Fixe no rodapé) levam ao início. */
  onHomeClick?: () => void;
}

/** Header com cor/logo por loja — equivalente a legacy/fixe-link-2 views/v2/partials/header.ejs. */
export function PublicInviteLayout({ passbook, children, onHomeClick }: PublicInviteLayoutProps) {
  const onColor = Boolean(passbook.backgroundColor);
  const [logoFailed, setLogoFailed] = useState(false);
  // Loja sem logo cadastrado: o nome dela no lugar, em vez do ícone de imagem quebrada.
  const logo = logoFailed ? (
    <span
      className="block max-w-[70vw] truncate px-2 text-lg font-semibold tracking-tight"
      style={{ color: passbook.foregroundColor || (onColor ? '#fff' : undefined) }}
    >
      {passbook.organizationName || passbook.clientName}
    </span>
  ) : (
    <img
      src={passbookLogoUrl(passbook.idPassbook)}
      alt={passbook.organizationName}
      className="h-12 w-auto object-contain"
      onError={() => setLogoFailed(true)}
    />
  );
  return (
    <div className="public-page flex flex-1 flex-col bg-muted/40">
      <header
        className="relative flex items-center justify-center px-4 py-6"
        style={passbook.backgroundColor ? { backgroundColor: passbook.backgroundColor } : undefined}
      >
        {onHomeClick ? (
          <button
            type="button"
            onClick={onHomeClick}
            aria-label="Ir para o início"
            className="flex min-h-12 items-center rounded-lg transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
          >
            {logo}
          </button>
        ) : (
          <div className="flex min-h-12 items-center">{logo}</div>
        )}
        <ThemeToggle
          className={cn(
            'absolute right-3 top-1/2 -translate-y-1/2',
            onColor && 'text-white hover:bg-white/10',
          )}
        />
      </header>

      <main className="container mx-auto flex max-w-2xl flex-1 flex-col px-4 py-6 sm:py-10">
        <div className="flex-1">{children}</div>
        <PublicInviteFooter onHomeClick={onHomeClick} />
      </main>
    </div>
  );
}
